import { useMemo, useState } from "react"
import type { ReactNode } from "react"
import { Outlet, useLocation } from "react-router"

import { useAuth } from "@/auth/AuthProvider"
import { Navbar } from "@/components/Navbar"
import { Sidebar, useSidebarExpandedState } from "@/components/Sidebar"
import { cn } from "@/lib/utils"

import { PageChromeContext } from "./pageChrome"

/**
 * Top-level chrome for authenticated pages: persistent left rail (desktop)
 * + top navbar, with a mobile overlay drawer for the same nav links.
 */
export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [sidebarExpanded, setSidebarExpanded] = useSidebarExpandedState()
  const [title, setTitle] = useState("Dashboard")
  const [leftAction, setLeftAction] = useState<ReactNode>(null)
  const [rightAction, setRightAction] = useState<ReactNode>(null)
  const { user, isAdmin, logout } = useAuth()
  const username = user?.username ?? null
  const { pathname } = useLocation()
  const isFixedViewport =
    pathname.startsWith("/adsmanager") ||
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/form") ||
    pathname.startsWith("/admin") ||
    pathname.startsWith("/offers") ||
    pathname.startsWith("/accounts")

  const chrome = useMemo(
    () => ({
      title,
      setTitle,
      leftAction,
      setLeftAction,
      rightAction,
      setRightAction,
    }),
    [title, leftAction, rightAction],
  )

  return (
    <PageChromeContext.Provider value={chrome}>
      <div className="flex h-dvh min-h-0 overflow-hidden bg-brand-bg text-brand-fg">
        <Sidebar
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
          expanded={sidebarExpanded}
          onExpandedChange={setSidebarExpanded}
          isAdmin={isAdmin}
        />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Navbar
            title={title}
            onOpenSidebar={() => setMobileOpen(true)}
            username={username}
            onLogout={logout}
            leftAction={leftAction}
            rightAction={rightAction}
          />
          <main
            className={cn(
              "flex min-h-0 w-full flex-1 flex-col",
              isFixedViewport
                ? "overflow-hidden"
                : "overflow-y-auto px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-6",
            )}
          >
            {isFixedViewport ? (
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <Outlet />
              </div>
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </PageChromeContext.Provider>
  )
}
